// src/pages/HomePage.jsx 
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getProductsByStatus } from '../services/productApi';
import ProductCard from '../components/ProductCard';
import '../styles/HomePage.css';
import { Dropdown } from 'react-bootstrap';
import ContactSection from '../components/ContactSection';

// Seeded random number generator for consistent monthly values
const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Stats Bar Component with Dynamic Random Monthly Calculations
const StatsBar = () => {
  const [stats, setStats] = useState({
    requests: 0,
    conversion: 0,
    avgDelivery: 0,
    professionals: 0
  });

  useEffect(() => {
    // Create a seed based on year and month (not day, so it's consistent per month)
    const now = new Date();
    const monthSeed = now.getFullYear() * 12 + now.getMonth();
    
    // Generate seeded random values for this month
    const rnd1 = seededRandom(monthSeed);
    const rnd2 = seededRandom(monthSeed + 1);
    const rnd3 = seededRandom(monthSeed + 2);
    const rnd4 = seededRandom(monthSeed + 3);

    // Requests: random between 200-300
    const calculatedRequests = Math.round(200 + rnd1 * 100);
    
    // Conversion rate: random between 60-75%
    const calculatedConversion = Math.round(60 + rnd2 * 15);

    // Average delivery time: random between 10-18 days
    const calculatedDelivery = Math.round(10 + rnd3 * 8);

    // Active professionals: random between 1200-2500
    const calculatedProfessionals = Math.round(1200 + rnd4 * 1300);

    setStats({
      requests: calculatedRequests,
      conversion: calculatedConversion,
      avgDelivery: calculatedDelivery,
      professionals: calculatedProfessionals
    });
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000) {
      return `+${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  return (
    <div className="stats-bar-container">
      <div className="container">
        <div className="stats-bar">
          <div className="stat-item">
            <div className="stat-num">{stats.requests}</div>
            <div className="stat-label">solicitudes este mes</div>
            <div className="stat-sublabel">crecimiento constante</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-num">{stats.conversion}%</div>
            <div className="stat-label">se hacen producto</div>
            <div className="stat-sublabel">tasa de conversión</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-num">{stats.avgDelivery}</div>
            <div className="stat-label">días entrega</div>
            <div className="stat-sublabel">promedio estimado</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-num">{formatNumber(stats.professionals)}</div>
            <div className="stat-label">profesionales AEC</div>
            <div className="stat-sublabel">comunidad activa</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {

  const [allApprovedProducts, setAllApprovedProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeCountries, setActiveCountries] = useState([]);
  const [activeSpecialties, setActiveSpecialties] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Skip API during react-snap pre-rendering
    if (navigator.userAgent === 'ReactSnap') return;
    const fetchProducts = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getProductsByStatus('APROBADO', 0, 100);
        const data = res.data.content || [];
        data.sort((a, b) => new Date(b.createdAt || b.idProducto) - new Date(a.createdAt || a.idProducto));
        setAllApprovedProducts(data);
        setDisplayedProducts(data);

        const cats = Array.from(new Set(data.flatMap(p => p.categorias || []))).filter(Boolean).sort();
        const coun = Array.from(new Set(data.map(p => p.pais).filter(Boolean))).sort();
        const specs = Array.from(new Set((data.flatMap(p => p.especialidades || []).filter(Boolean)))).sort();
        setAvailableCategories(cats);
        setAvailableCountries(coun.slice(0, 5));
        setAvailableSpecialties(specs);
        setLoading(false);
      } catch (e) {
        // Silently fail - show rest of page without products
        console.error('Error al cargar productos:', e);
        setAllApprovedProducts([]);
        setDisplayedProducts([]);
        setError('No se pudieron cargar los productos. Por favor intenta más tarde.');
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = allApprovedProducts
      .filter(p =>
        (p.nombre || p.title || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter(p =>
        activeCategories.length === 0 || activeCategories.some(cat => (p.categorias || []).includes(cat))
      )
      .filter(p =>
        activeCountries.length === 0 || activeCountries.includes(p.pais)
      )
      .filter(p =>
        activeSpecialties.length === 0 || activeSpecialties.some(sp => (p.especialidades || []).includes(sp))
      );
    setDisplayedProducts(filtered);
  }, [
    searchTerm,
    activeCategories,
    activeCountries,
    activeSpecialties,
    allApprovedProducts
  ]);

  const handleCheckboxChange = (filterType, value) => {
    const setter = {
      categories: setActiveCategories,
      countries: setActiveCountries,
      specialties: setActiveSpecialties
    }[filterType];

    const activeFilters = {
      categories: activeCategories,
      countries: activeCountries,
      specialties: activeSpecialties
    }[filterType];

    if (activeFilters.includes(value)) {
      setter(activeFilters.filter(item => item !== value));
    } else {
      setter([...activeFilters, value]);
    }
  };

  const getDropdownTitle = (filterType, activeFilters, defaultTitle) => {
    if (activeFilters.length === 0) {
      return defaultTitle;
    } else if (activeFilters.length === 1) {
      return activeFilters[0];
    } else {
      return `${activeFilters.length} ${defaultTitle.split(':')[0].trim()}s seleccionados`;
    }
  };

  return (
    <main>
      <Helmet>
        <title>AECBlock | +300 Herramientas BIM para Arquitectos e Ingenieros en Latinoamérica</title>
        <meta name="description" content="Accede a +300 herramientas técnicas validadas: familias BIM, scripts, plantillas de cálculo con normativa NEC y AASHTO. Comunidad de +1.800 profesionales AEC en Latinoamérica." />
        <link rel="canonical" href="https://aecblock.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AECBlock | +300 Herramientas BIM Validadas para AEC en Latinoamérica" />
        <meta property="og:description" content="Herramientas BIM validadas por especialistas: familias Revit, scripts, plantillas de cálculo con normativa NEC y AASHTO. +1.800 Arquitectos, Ingenieros & Constructores (AEC) activos en Latinoamérica." />
        <meta property="og:url" content="https://aecblock.com/" />
        <meta property="og:image" content="https://aecblock.com/default-og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AECBlock | +300 Herramientas BIM Validadas para AEC en Latinoamérica" />
        <meta name="twitter:description" content="Familias BIM, scripts y plantillas de cálculo validadas por ingenieros y arquitectos. Normativa NEC, AASHTO. Comunidad de +1.800 profesionales AEC en Latinoamérica." />
      </Helmet>
      <section className="hero-section">
        <div className="container">
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--white)',
            border: '1px solid var(--gray-200)',
            borderRadius: '40px',
            padding: '6px 16px',
            fontSize: '13px',
            fontWeight: '500',
            color: 'var(--gray-600)',
            marginBottom: '28px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <span style={{
              width: '7px',
              height: '7px',
              background: 'var(--green)',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'pulse 2s infinite',
            }}></span>
             +1.800 Arquitectos, Ingenieros & Constructores (AEC) activos en Latinoamérica
          </div>

          <h1 className="hero-title">
            Deja de reinventar<br />
            la rueda en <span className="highlight-text">cada proyecto</span>
          </h1>

          <p className="hero-subtitle">
            Accede a +300 herramientas técnicas validadas por ingenieros y arquitectos reales. Familias BIM, scripts, plantillas de cálculo y más.
          </p>

           <div style={{
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             flexWrap: 'wrap',
             gap: '12px',
             marginBottom: '48px',
           }}>
             <Link to="/catalog?tipo=gratis" className="btn-explore">
               Ver herramientas gratuitas →
             </Link>
             <a
               href="https://wa.me/593999527013?text=Hola%2C%20quisiera%20consultar%20sobre%20herramientas%20AECBlock"
               target="_blank"
               rel="noopener noreferrer"
               className="btn-explore"
               style={{
                 background: '#25D366',
                 textDecoration: 'none',
               }}
             >
               Consultar por WhatsApp
             </a>
            <button
              onClick={() => {
                document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-explore"
              style={{
                background: 'transparent',
                color: 'var(--black)',
                border: '1.5px solid var(--gray-200)',
                cursor: 'pointer',
              }}
            >
              Pedir una herramienta
            </button>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '24px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--gray-600)' }}>
              <span style={{ color: 'var(--yellow)' }}>★★★★★</span>
              <span>4.8 / 5 promedio</span>
            </div>
            <div style={{ width: '1px', height: '18px', background: 'var(--gray-200)' }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--gray-600)' }}>
              <span>✓</span>
              <span>100% creado por profesionales AEC</span>
            </div>
            <div style={{ width: '1px', height: '18px', background: 'var(--gray-200)' }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--gray-600)' }}>
              <span>🌎</span>
              <span>Disponible en Ecuador, Perú, Colombia y más</span>
            </div>
          </div>
        </div>
       </section>

       {/* ── Catalog Header Section ── */}
       <section className="catalog-header-section">
         <div className="container">
           <div className="catalog-header-content">
             <span className="section-label">CATÁLOGO</span>
             <h2 className="section-title">Herramientas listas<br />para usar hoy</h2>
           </div>
         </div>
       </section>

        {/* ── Search & Filter Strip ── */}
        <section className="search-section">
         <div className="container">
           <div className="search-row">
             <Link to="/catalog" className="btn-explore-catalog">
               Explorar Catálogo
             </Link>

             <div className="search-wrapper">
               <input
                 type="text"
                 className="form-control search-input"
                 placeholder="Buscar herramientas por nombre, software o especialidad..."
                 value={searchTerm}
                 onChange={e => setSearchTerm(e.target.value)}
               />
               <svg
                 className="search-icon"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 width="16"
                 height="16"
                 fill="none"
                 stroke="currentColor"
                 strokeWidth="2"
                 strokeLinecap="round"
                 strokeLinejoin="round"
               >
                 <circle cx="11" cy="11" r="8" />
                 <path d="m21 21-4.3-4.3" />
               </svg>
             </div>

            {/* Filtro de Países */}
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown-toggle">
                {getDropdownTitle('countries', activeCountries, 'País')}
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-wide">
                {availableCountries.map(cn => (
                  <Dropdown.Item as="button" key={cn} onClick={e => e.stopPropagation()} className="dropdown-item-checkbox">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`country-check-${cn}`}
                        checked={activeCountries.includes(cn)}
                        onChange={() => handleCheckboxChange('countries', cn)}
                      />
                      <label className="form-check-label" htmlFor={`country-check-${cn}`}>
                        {cn}
                      </label>
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Filtro de Categorías */}
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown-toggle">
                {getDropdownTitle('categories', activeCategories, 'Categoría')}
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-wide">
                {availableCategories.map(cat => (
                  <Dropdown.Item as="button" key={cat} onClick={e => e.stopPropagation()} className="dropdown-item-checkbox">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`category-check-${cat}`}
                        checked={activeCategories.includes(cat)}
                        onChange={() => handleCheckboxChange('categories', cat)}
                      />
                      <label className="form-check-label" htmlFor={`category-check-${cat}`}>
                        {cat}
                      </label>
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Filtro de Especialidades */}
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" className="filter-dropdown-toggle">
                {getDropdownTitle('specialties', activeSpecialties, 'Especialidad')}
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-wide">
                {availableSpecialties.map(sp => (
                  <Dropdown.Item as="button" key={sp} onClick={e => e.stopPropagation()} className="dropdown-item-checkbox">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`specialty-check-${sp}`}
                        checked={activeSpecialties.includes(sp)}
                        onChange={() => handleCheckboxChange('specialties', sp)}
                      />
                      <label className="form-check-label" htmlFor={`specialty-check-${sp}`}>
                        {sp}
                      </label>
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </section>

      <section id="catalogo" className="catalog-section py-5">
        <div className="container">
          {/* -- Product Grid -- */}
          {loading ? (
            <div className="text-center py-5 text-muted">Cargando productos…</div>
          ) : error ? (
            <div className="text-center py-4 text-danger">{error}</div>
          ) : (
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4">
              {displayedProducts.length > 0 ? (
                displayedProducts.slice(0, 8).map(product => (
                  <div key={product.idProducto} className="col">
                    <ProductCard product={product} />
                  </div>
                ))
              ) : (
                <p className="text-center col-12 text-muted">
                  No hay productos que coincidan con tus criterios.
                </p>
              )}
            </div>
          )}
        </div>
      </section>
      <section id="beneficios" className="benefits-section">
        <div className="container">
          <div style={{maxWidth:'900px'}}>
            <span className="section-label">Por qué AECBlock</span>
            <h2 className="section-title">Construido por AEC,<br/>para AEC.</h2>
            <p className="section-sub">Cada herramienta es revisada por especialistas activos. Nada de recursos genéricos.</p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">⚡</div>
              <div className="benefit-title">Ahorra hasta 60% del tiempo de modelado</div>
              <div className="benefit-desc">Scripts y familias listas para insertar en tu flujo técnico sin configuraciones extras ni curva de aprendizaje.</div>
              <div className="testimonial-block">
                <div className="testimonial-quote">"La plantilla de verificación de columnas me ahorró un día completo de trabajo en un proyecto con 48 secciones distintas."</div>
                <div className="testimonial-author">Ing. Pablo M. — Quito, Ecuador</div>
              </div>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">✅</div>
              <div className="benefit-title">Calidad validada por especialistas</div>
              <div className="benefit-desc">Cada recurso pasa por revisión técnica antes de publicarse. Sin archivos corruptos, sin parámetros erróneos, sin sorpresas.</div>
              <div className="testimonial-block">
                <div className="testimonial-quote">"Descargué la familia de vigas tipo I y funcionó en Revit 2024 sin ningún ajuste. Eso no pasa en otros sitios."</div>
                <div className="testimonial-author">Arq. Camila R. — Bogotá, Colombia</div>
              </div>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">🌎</div>
              <div className="benefit-title">Normativa local, no solo estándares internacionales</div>
              <div className="benefit-desc">Herramientas diseñadas con normativa NEC, AASHTO en español y contexto latinoamericano. No todo lo que funciona en EE.UU. aplica aquí.</div>
              <div className="testimonial-block">
                <div className="testimonial-quote">"La hoja de diseño de pavimentos con AASHTO 93 adaptada a normativa ecuatoriana me salvó en un proyecto vial."</div>
                <div className="testimonial-author">Ing. Carlos V. — Guayaquil, Ecuador</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ContactSection />
      
      <StatsBar />
    </main>
  );
};

export default HomePage;
